/**
 * WorkflowTracker — horizontal stage stepper for the case workflow (FND-006).
 *
 * Renders the enrollment lifecycle stages with per-stage state:
 *   - complete  ✓  (filled, success)
 *   - current      (filled, action teal — the active stage)
 *   - locked    🔒 (dependency-gated; advances only when prior tasks complete)
 *   - upcoming     (neutral, not yet reachable)
 *
 * Built now for the case page (used to depth in a later phase).
 */
import type { CSSProperties } from 'react';
import { colors } from '../theme';

export type StageState = 'complete' | 'current' | 'locked' | 'upcoming';

export interface WorkflowStage {
  /** Stable stage key. */
  key: string;
  label: string;
  state: StageState;
}

export interface WorkflowTrackerProps {
  stages: WorkflowStage[];
  /** Optional click handler (e.g. revert to an earlier complete stage). */
  onStageClick?: (stage: WorkflowStage) => void;
  style?: CSSProperties;
}

function nodeColors(state: StageState): { bg: string; border: string; fg: string } {
  switch (state) {
    case 'complete':
      return { bg: colors.success, border: colors.success, fg: '#FFFFFF' };
    case 'current':
      return { bg: colors.action, border: colors.action, fg: '#FFFFFF' };
    case 'locked':
      return { bg: colors.surface, border: colors.border, fg: colors.textMuted };
    default:
      return { bg: colors.white, border: colors.border, fg: colors.textMuted };
  }
}

function glyph(state: StageState, index: number): string {
  if (state === 'complete') return '✓';
  if (state === 'locked') return '🔒';
  return String(index + 1);
}

export function WorkflowTracker({ stages, onStageClick, style }: WorkflowTrackerProps) {
  return (
    <ol style={{ ...track, ...style }}>
      {stages.map((stage, i) => {
        const c = nodeColors(stage.state);
        const clickable = !!onStageClick && stage.state !== 'locked' && stage.state !== 'upcoming';
        return (
          <li key={stage.key} style={item}>
            {i > 0 && (
              <span
                style={{
                  ...connector,
                  background: stage.state === 'complete' || stage.state === 'current' ? colors.success : colors.border,
                }}
                aria-hidden
              />
            )}
            <button
              type="button"
              disabled={!clickable}
              onClick={clickable ? () => onStageClick?.(stage) : undefined}
              style={{
                ...nodeWrap,
                cursor: clickable ? 'pointer' : 'default',
              }}
            >
              <span
                style={{
                  ...node,
                  background: c.bg,
                  border: `2px solid ${c.border}`,
                  color: c.fg,
                  fontWeight: stage.state === 'current' ? 700 : 600,
                }}
              >
                {glyph(stage.state, i)}
              </span>
              <span
                style={{
                  ...label,
                  color: stage.state === 'upcoming' || stage.state === 'locked' ? colors.textMuted : colors.textPrimary,
                  fontWeight: stage.state === 'current' ? 700 : 500,
                }}
              >
                {stage.label}
              </span>
            </button>
          </li>
        );
      })}
    </ol>
  );
}

const track: CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  listStyle: 'none',
  margin: 0,
  padding: 0,
  gap: 0,
  overflowX: 'auto',
};
const item: CSSProperties = { display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0 };
const connector: CSSProperties = { height: 2, flex: 1, marginTop: 13, minWidth: 16 };
const nodeWrap: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 6,
  background: 'none',
  border: 'none',
  padding: 0,
  textAlign: 'center',
  flexShrink: 0,
  maxWidth: 120,
};
const node: CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: '50%',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.8rem',
};
const label: CSSProperties = { fontSize: '0.76rem', lineHeight: 1.25, padding: '0 4px' };
